// ═══════════════════════════════════════════════════════════════════════════════
//  src/utils/afiliado.js
//  EL CÓDIGO DEL PROFESOR, DESDE QUE SE PULSA EL ENLACE HASTA QUE HAY CUENTA
//
//  Un profesor reparte `https://nsl.app/?ref=AC4K9D`. Entre ese clic y el
//  registro puede pasar de todo: el visitante mira precios, se va, vuelve al día
//  siguiente, entra por otra página. El código tiene que sobrevivir a ese
//  paseo, porque de él depende que ese profesor cobre.
//
//  ── POR QUÉ SE CAPTURA GLOBALMENTE Y NO EN LA PANTALLA DE REGISTRO ──────────
//  Porque el enlace casi nunca apunta al registro. Apunta a la portada, a
//  precios o a la landing, que es donde tiene sentido mandar a alguien que
//  todavía no sabe qué es NSL. Si sólo se leyera en `/register`, se perdería el
//  código de todo el que hiciera una parada por el camino — que son casi todos.
//
//  ── LAS DOS DECISIONES DE NEGOCIO QUE HAY AQUÍ ──────────────────────────────
//  Están en una constante cada una, a la vista, porque son de Rob y no mías:
//
//    · DIAS_VALIDO — cuánto dura el código guardado.
//    · El último enlace pulsado manda (ver `guarda`).
//
//  ── LO QUE ESTE FICHERO NO DECIDE ───────────────────────────────────────────
//  A quién pertenece el código. Aquí sólo viaja el texto; quién es el profesor
//  detrás lo resuelve el servidor al registrar. Si esta pantalla mandara un
//  identificador de profesor, cualquiera podría atribuirse alumnos escribiendo
//  un número en las herramientas del navegador.
// ═══════════════════════════════════════════════════════════════════════════════

const CLAVE = 'nsl_ref';

/** Cuánto tiempo se recuerda el código desde que se pulsa el enlace.
 *
 *  30 días es lo habitual en programas de afiliados y es un número de negocio,
 *  no técnico: es cuánto tiempo se considera que el profesor «trajo» a alguien
 *  que todavía se lo está pensando. Se cambia aquí y en ningún otro sitio. */
const DIAS_VALIDO = 30;

/** El código es de un alfabeto cerrado y mide 6. Se comprueba aquí también —no
 *  por seguridad, que esa está en el servidor— sino para no guardar basura:
 *  cualquiera puede escribir `?ref=<script>` en la barra de direcciones. */
const FORMA = /^[ACDEFGHJKMNPQRTUVWXY34679]{6}$/;

function normaliza(texto) {
  return String(texto || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 16);
}

/** Lo lee de la URL y lo guarda. Se llama una vez al arrancar la aplicación.
 *
 *  Con eso basta: pulsar el enlace de un profesor es una carga completa de la
 *  página, así que el código está en la URL justo en ese arranque. La navegación
 *  interna de la aplicación no lo lleva ni tiene por qué. */
export function capturaRef() {
  try {
    const url = new URLSearchParams(window.location.search);
    const crudo = url.get('ref') || url.get('REF');
    if (crudo) guarda(crudo);
  } catch { /* URL rara: no se guarda nada y la app sigue */ }
}

/** Guarda el código si tiene la forma correcta.
 *
 *  MANDA EL ÚLTIMO ENLACE PULSADO. Si alguien llega con el código de un profesor
 *  y días después con el de otro, se queda el segundo: es el enlace que de
 *  verdad lo trajo esta vez. Es la regla habitual del sector y es la que se
 *  puede explicar a un profesor sin que suene arbitraria.
 *
 *  (La alternativa —que mande el primero— premia a quien lo descubrió. Si Rob
 *  la prefiere, se cambia esta función y nada más.) */
export function guarda(crudo) {
  const codigo = normaliza(crudo);
  if (!FORMA.test(codigo)) return;
  try {
    localStorage.setItem(CLAVE, JSON.stringify({ codigo, cuando: Date.now() }));
  } catch { /* modo privado: no se guarda y el registro va sin código */ }
}

/** El código guardado, o `null` si no hay o ya caducó. */
export function refGuardado() {
  try {
    const crudo = localStorage.getItem(CLAVE);
    if (!crudo) return null;
    const { codigo, cuando } = JSON.parse(crudo);
    if (!codigo || !cuando) return null;
    const dias = (Date.now() - cuando) / 86400000;
    if (dias > DIAS_VALIDO) {
      localStorage.removeItem(CLAVE);
      return null;
    }
    return FORMA.test(codigo) ? codigo : null;
  } catch {
    return null;
  }
}

/** Se llama cuando ya hay cuenta: el código cumplió su función.
 *
 *  Si se quedara, el siguiente que se registrara en ese mismo navegador —un
 *  hermano, un compañero de trabajo, el mismo alumno con otra cuenta— quedaría
 *  atribuido al profesor sin que nadie le hubiera pasado nada. */
export function olvidaRef() {
  try { localStorage.removeItem(CLAVE); } catch { /* nada que hacer */ }
}
